import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { signupThunk } from '../redux/userSlice'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { Input } from '../components/ui/input'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader } from '../components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command'
import { X, Check } from 'lucide-react'
import { cn } from '../lib/utils'
import { searchSchools } from '../lib/api'
import SportsMultiSelect from '../components/SportsMultiSelect'
import { AppDispatch, RootState } from '../store'
import { Checkbox } from '../components/ui/checkbox'
import { CURRENT_LEGAL_VERSION } from '@adthlete/shared-frontend'

const useAppDispatch = () => useDispatch<AppDispatch>()

interface School {
  id: number
  name: string
}

const MINIMUM_ATHLETE_AGE = 18

function calculateAge(dateOfBirth: string): number {
  const dob = new Date(dateOfBirth)
  const now = new Date()
  let age = now.getFullYear() - dob.getFullYear()
  const monthDiff = now.getMonth() - dob.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
    age -= 1
  }
  return age
}

const Onboarding: React.FC = () => {
  const [fullName, setFullName] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [schools, setSchools] = useState<School[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedSchool, setSelectedSchool] = useState<School | null>(null)
  const [schoolOpen, setSchoolOpen] = useState(false)
  const [selectedSports, setSelectedSports] = useState<number[]>([])
  const [socialHandles, setSocialHandles] = useState<{ platform: string; handle: string }[]>([])
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [error, setError] = useState('')
  const dispatch = useAppDispatch()
  const isLoading = useSelector((state: RootState) => state.user.loading)
  const navigate = useNavigate()
  const { athlete } = useAuth()

  // If already signed in, redirect to home
  useEffect(() => {
    if (athlete) {
      navigate('/', { replace: true })
    }
  }, [athlete, navigate])

  // Check if we have signup data from the sign-up form
  useEffect(() => {
    const signupEmail = sessionStorage.getItem('signupEmail')
    const signupPassword = sessionStorage.getItem('signupPassword')

    if (!signupEmail || !signupPassword) {
      // If no signup data, redirect to sign-in page
      navigate('/signin')
    }
  }, [navigate])

  // Load saved form data from sessionStorage
  useEffect(() => {
    const savedForm = sessionStorage.getItem('onboardingFormData')
    if (savedForm) {
      try {
        const parsed = JSON.parse(savedForm)
        if (parsed.fullName) setFullName(parsed.fullName)
        if (parsed.dateOfBirth) setDateOfBirth(parsed.dateOfBirth)
        if (parsed.selectedSports) setSelectedSports(parsed.selectedSports)
        if (parsed.socialHandles) setSocialHandles(parsed.socialHandles)
        if (parsed.agreedToTerms) setAgreedToTerms(parsed.agreedToTerms)
        if (parsed.selectedSchool) setSelectedSchool(parsed.selectedSchool)
      } catch (e) {
        console.error('Failed to parse saved form data', e)
      }
    }
  }, [])

  // Save form data to sessionStorage on change
  useEffect(() => {
    const formData = {
      fullName,
      dateOfBirth,
      selectedSports,
      socialHandles,
      agreedToTerms,
      selectedSchool,
    }
    sessionStorage.setItem('onboardingFormData', JSON.stringify(formData))
  }, [fullName, dateOfBirth, selectedSports, socialHandles, agreedToTerms, selectedSchool])

  // Debounced school search
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (searchQuery.length >= 2) {
        setIsSearching(true)
        try {
          const results = await searchSchools(searchQuery)
          setSchools(results)
        } catch (err) {
          console.error('School search failed:', err)
          setSchools([])
        } finally {
          setIsSearching(false)
        }
      } else {
        setSchools([])
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [searchQuery])

  const availablePlatforms = ['Instagram', 'TikTok', 'YouTube']
  const usedPlatforms = socialHandles.map((sh) => sh.platform)
  const remainingPlatforms = availablePlatforms.filter((p) => !usedPlatforms.includes(p))

  const isFormValid =
    fullName.trim() !== '' &&
    dateOfBirth !== '' &&
    selectedSports.length > 0 &&
    socialHandles.length > 0 &&
    socialHandles.every((sh) => sh.handle.trim() !== '') &&
    agreedToTerms

  const addSocialHandle = (platform: string) => {
    setSocialHandles([...socialHandles, { platform, handle: '' }])
  }

  const updateSocialHandle = (index: number, handle: string) => {
    const updated = [...socialHandles]
    updated[index].handle = handle
    setSocialHandles(updated)
  }

  const removeSocialHandle = (index: number) => {
    setSocialHandles(socialHandles.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!isFormValid) {
      setError('Please complete all required fields')
      return
    }

    if (calculateAge(dateOfBirth) < MINIMUM_ATHLETE_AGE) {
      setError(
        `You must be at least ${MINIMUM_ATHLETE_AGE} years old to create an athlete account`,
      )
      return
    }

    const signupEmail = sessionStorage.getItem('signupEmail')
    const signupPassword = sessionStorage.getItem('signupPassword')

    if (!signupEmail || !signupPassword) {
      setError('Session expired. Please sign up again.')
      navigate('/signin')
      return
    }

    try {
      const result = await dispatch(
        signupThunk({
          name: fullName.trim(),
          email: signupEmail,
          password: signupPassword,
          phone: '', // Optional field
          school_id: selectedSchool?.id || null,
          sports: selectedSports,
          socialHandles,
          date_of_birth: dateOfBirth,
          agreed_to_terms: agreedToTerms,
          legal_version: CURRENT_LEGAL_VERSION,
        }),
      ).unwrap()

      if (result) {
        // Store social handles for later use (could be sent to backend in future)
        sessionStorage.setItem('socialHandles', JSON.stringify(socialHandles))

        // Clear the stored signup data and form data
        sessionStorage.removeItem('onboardingFormData')
        sessionStorage.removeItem('signupEmail')
        sessionStorage.removeItem('signupEmail')
        sessionStorage.removeItem('signupPassword')
        // Store email for the verification page
        sessionStorage.setItem('userEmailForVerification', signupEmail)
        // Redirect to the email verification pending page
        navigate('/verify-email', { state: { email: signupEmail } })
      }
    } catch (err: unknown) {
      const error = err as { message?: string }
      setError(error.message || 'Registration failed. Please try again.')
    }
  }

  const handleSportsChange = (sportIds: number[]) => {
    setSelectedSports(sportIds)
  }

  return (
    <div className="flex items-center justify-center min-h-screen relative">
      <div className="fixed inset-0 -z-10 bg-linear-to-br from-[#000000] via-[#0a1e29] to-[#003b4d]"></div>

      <div className="w-full max-w-md px-4">
        <Card variant="dark">
          <CardHeader className="text-center pb-0">
            <div className="flex justify-center mb-6">
              <img
                src="https://adthlete.us-iad-1.linodeobjects.com/public/adthlete_logomark.png"
                alt="Athios Logo"
                className="w-10 h-10"
              />
            </div>
            <CardDescription className="mt-2 text-gray-400">
              Complete your Athios profile
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {error && <p className="text-red-500 mb-4 text-sm text-center">{error}</p>}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  type="text"
                  placeholder="Your Full Name *"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="bg-white/5 text-white border-white/10 focus:border-[#C0FD72] focus:ring-[#C0FD72]/20"
                  required
                />
              </div>

              <div>
                <label htmlFor="date-of-birth" className="text-sm text-gray-400">
                  Date of Birth *
                </label>
                <Input
                  id="date-of-birth"
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="mt-2 bg-white/5 text-white border-white/10 focus:border-[#C0FD72] focus:ring-[#C0FD72]/20"
                  required
                />
              </div>

              <div>
                <Popover open={schoolOpen} onOpenChange={setSchoolOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={schoolOpen}
                      className="w-full justify-between bg-white/5 text-white border-white/10 hover:bg-white/10 hover:text-white focus:border-[#C0FD72] focus:ring-[#C0FD72]/20"
                    >
                      {selectedSchool ? selectedSchool.name : 'School Name'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-full p-0 bg-[#1a2b3c] border-white/10"
                    align="start"
                  >
                    <Command className="bg-[#1a2b3c]">
                      <CommandInput
                        placeholder="Search school..."
                        value={searchQuery}
                        onValueChange={setSearchQuery}
                        className="text-white border-white/10"
                      />
                      <CommandList>
                        {isSearching && (
                          <CommandEmpty className="text-gray-400 py-6 text-center text-sm">
                            Searching...
                          </CommandEmpty>
                        )}
                        {!isSearching && searchQuery.length >= 2 && schools.length === 0 && (
                          <CommandEmpty className="text-gray-400 py-6 text-center text-sm">
                            No school found.
                          </CommandEmpty>
                        )}
                        {!isSearching && searchQuery.length < 2 && (
                          <CommandEmpty className="text-gray-400 py-6 text-center text-sm">
                            Type at least 2 characters to search.
                          </CommandEmpty>
                        )}
                        {schools.length > 0 && (
                          <CommandGroup className="text-white">
                            {schools.map((school) => (
                              <CommandItem
                                key={school.id}
                                value={school.name}
                                onSelect={() => {
                                  setSelectedSchool(school)
                                  setSchoolOpen(false)
                                }}
                                className="text-white hover:bg-white/10 cursor-pointer"
                              >
                                <Check
                                  className={cn(
                                    'mr-2 h-4 w-4',
                                    selectedSchool?.id === school.id ? 'opacity-100' : 'opacity-0',
                                  )}
                                />
                                {school.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <SportsMultiSelect
                label="Sports *"
                description="What sports do you participate in?"
                selectedSportIds={selectedSports}
                onSportsChange={handleSportsChange}
                placeholder="Add a sport..."
              />

              <div className="space-y-2">
                <label className="text-sm text-gray-400">
                  Social Handles (at least 1 required) *
                </label>

                {socialHandles.map((social, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-white text-sm">
                        {social.platform}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeSocialHandle(index)}
                        className="p-2 hover:bg-white/5 rounded-md transition-colors"
                      >
                        <X className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                    <Input
                      type="text"
                      placeholder={`@${social.platform.toLowerCase()}`}
                      value={social.handle}
                      onChange={(e) => updateSocialHandle(index, e.target.value)}
                      className="bg-white/5 text-white border-white/10 focus:border-[#C0FD72] focus:ring-[#C0FD72]/20"
                      required
                    />
                  </div>
                ))}

                {remainingPlatforms.length > 0 && (
                  <Select key={socialHandles.length} onValueChange={addSocialHandle}>
                    <SelectTrigger className="bg-white/5 text-white border-white/10 focus:border-[#C0FD72] focus:ring-[#C0FD72]/20">
                      <SelectValue
                        placeholder={
                          socialHandles.length === 0 ? 'Select social platform' : 'Add another'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a2b3c] border-white/10 text-white">
                      {remainingPlatforms.map((platform) => (
                        <SelectItem
                          key={platform}
                          value={platform}
                          className="focus:bg-white/10 focus:text-white"
                        >
                          {platform}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="flex items-start space-x-3 my-4">
                <Checkbox
                  id="agree-terms"
                  checked={agreedToTerms}
                  onCheckedChange={(checked) => setAgreedToTerms(checked as boolean)}
                  className="border-white/20 data-[state=checked]:bg-[#C0FD72] data-[state=checked]:border-[#C0FD72] mt-1"
                />
                <label
                  htmlFor="agree-terms"
                  className="text-sm text-gray-300 leading-relaxed cursor-pointer"
                >
                  I agree to the{' '}
                  <Link to="/terms" className="text-[#C0FD72] hover:text-[#a8e45a] underline">
                    Terms of Use and Privacy Policy
                  </Link>{' '}
                  outlined below. I understand that I am responsible for complying with all
                  applicable NIL regulations and institutional policies.
                </label>
              </div>

              <Button
                type="submit"
                className={`w-full font-medium transition-all ${
                  isFormValid
                    ? 'bg-[#C0FD72] hover:bg-[#a8e45a] text-black'
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
                }`}
                disabled={!isFormValid || isLoading}
              >
                {isLoading ? 'Creating Account...' : 'Complete Registration'}
              </Button>

              <div className="text-center">
                <Link
                  to="/signin"
                  className="text-sm text-gray-400 hover:text-[#C0FD72] transition-colors"
                >
                  Already have an account? Sign in
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default Onboarding
